export type WalletProviderSlug = string;

export type WalletProProviderAccessLike = {
  mode?: unknown;
  providers?: readonly unknown[];
  clusterTypes?: readonly unknown[];
  resolvedParameters?: Record<string, string>;
};

export type WalletProProviderHardwareLike = {
  providers?: readonly unknown[];
  proAccessOptions?: readonly WalletProProviderAccessLike[];
};

export type WalletProviderCapacityLike = {
  stockAvailable?: unknown;
  launchable?: unknown;
};

export type WalletProviderHardwarePayloadLike = {
  hardware_id?: unknown;
  hardwareId?: unknown;
  hardware_label?: unknown;
  hardwareLabel?: unknown;
  gpu_count?: unknown;
  gpuCount?: unknown;
  stock_available?: unknown;
  stockAvailable?: unknown;
  launchable?: unknown;
  best_price_per_hour?: unknown;
  bestPricePerHour?: unknown;
  best_buyer_price_micros?: unknown;
  bestBuyerMicros?: unknown;
};

export type WalletMarketOfferLike = {
  option_id?: unknown;
  source?: unknown;
  provider_slug?: unknown;
  provider_name?: unknown;
  gpu?: unknown;
  gpu_type?: unknown;
  gpu_count?: unknown;
  flavor_name?: unknown;
  region_name?: unknown;
  stock_available?: unknown;
  buyer_unit_price_micros?: unknown;
  price_per_hour?: unknown;
};

export type WalletMarketHardwareOption = {
  hardware_id: string;
  hardware_label: string;
  gpu_count: number;
  stock_available: number;
  launchable: boolean;
  capacity_status: "available";
  best_price_per_hour: number;
  best_buyer_price_micros: number;
  provider_count: number;
  providers: string[];
  regions: string[];
};

export const WALLET_PROVIDER_HARDWARE_API_PATH = "/api/v1/wallet/provider-hardware";
export const WALLET_PROVIDER_HARDWARE_LEGACY_API_PATH = "/api/wallet/accounts?action=provider_job_hardware";

export function walletHardwareOptionLaunchable(option: WalletProviderCapacityLike) {
  if (typeof option.launchable === "boolean") return option.launchable;
  return Number(option.stockAvailable || 0) > 0;
}

export function walletProviderHourlyPriceSortValue(value: unknown) {
  const price = Number(value);
  return Number.isFinite(price) && price > 0 ? price : Number.POSITIVE_INFINITY;
}

function positiveWalletNumber(value: unknown) {
  if (typeof value === "boolean") return 0;
  const number = Number(value || 0);
  return Number.isFinite(number) && number > 0 ? number : 0;
}

export function walletProviderHardwareOptionIsLivePriced(
  option: WalletProviderHardwarePayloadLike,
) {
  const stockAvailable = positiveWalletNumber(
    option.stock_available ?? option.stockAvailable,
  );
  const launchable = typeof option.launchable === "boolean"
    ? option.launchable
    : stockAvailable > 0;
  const buyerHourly = positiveWalletNumber(
    option.best_buyer_price_micros ?? option.bestBuyerMicros,
  ) / 1_000_000;
  const hourlyPrice = buyerHourly || positiveWalletNumber(
    option.best_price_per_hour ?? option.bestPricePerHour,
  );
  return launchable && hourlyPrice > 0;
}

function normalizeWalletMarketHardwareKey(value: unknown) {
  const text = String(value || "")
    .trim()
    .toUpperCase()
    .replace(/GIB|GB/g, "G")
    .replace(/[^A-Z0-9]+/g, " ")
    .trim();
  if (/\bH200\b/.test(text)) return "H200";
  if (/\bH100\b/.test(text)) return "H100";
  if (/\bA100\b/.test(text)) {
    if (/\b80\s*G\b/.test(text)) return "A10080G";
    if (/\b40\s*G\b/.test(text)) return "A10040G";
    return "A100";
  }
  if (/\bA10G?\b/.test(text)) return "A10G";
  if (/\bL40S\b/.test(text)) return "L40S";
  if (/\bL40\b/.test(text)) return "L40";
  if (/\bA6000\b/.test(text)) return "A6000";
  return text
    .replace(/NVIDIA|GEFORCE|TESLA|RTX|GPU/g, "")
    .replace(/[^A-Z0-9]/g, "");
}

function walletProviderHardwareOptionKey(option: WalletProviderHardwarePayloadLike) {
  const label = option.hardware_label
    ?? option.hardwareLabel
    ?? option.hardware_id
    ?? option.hardwareId;
  const hardwareKey = normalizeWalletMarketHardwareKey(label);
  const gpuCount = Math.max(1, Math.round(positiveWalletNumber(
    option.gpu_count ?? option.gpuCount,
  ) || 1));
  return hardwareKey ? `${hardwareKey}:${gpuCount}` : "";
}

function walletMarketUniqueValues(values: Iterable<string>) {
  return [...values].sort((left, right) => left.localeCompare(right));
}

export function walletHardwareOptionsFromMarketOffers(
  offers: readonly WalletMarketOfferLike[] | null | undefined,
): WalletMarketHardwareOption[] {
  type Accumulator = {
    hardwareID: string;
    hardwareLabel: string;
    gpuCount: number;
    stockAvailable: number;
    bestHourlyPrice: number;
    bestBuyerMicros: number;
    providers: Map<string, string>;
    regions: Map<string, string>;
  };

  const byHardware = new Map<string, Accumulator>();
  (offers || []).forEach((offer) => {
    const hardwareLabel = String(
      offer.gpu || offer.gpu_type || offer.flavor_name || offer.option_id || "",
    ).trim();
    const hardwareKey = normalizeWalletMarketHardwareKey(hardwareLabel);
    const gpuCount = Math.max(1, Math.round(positiveWalletNumber(offer.gpu_count) || 1));
    const stockAvailable = Math.max(0, Math.round(positiveWalletNumber(offer.stock_available)));
    const buyerMicros = positiveWalletNumber(offer.buyer_unit_price_micros);
    const buyerHourlyPrice = buyerMicros / 1_000_000;
    const rawHourlyPrice = positiveWalletNumber(offer.price_per_hour);
    const hourlyPrice = buyerHourlyPrice || rawHourlyPrice;
    if (!hardwareKey || stockAvailable <= 0 || hourlyPrice <= 0) return;

    const key = `${hardwareKey}:${gpuCount}`;
    const current = byHardware.get(key) || {
      hardwareID: hardwareLabel,
      hardwareLabel,
      gpuCount,
      stockAvailable: 0,
      bestHourlyPrice: 0,
      bestBuyerMicros: 0,
      providers: new Map<string, string>(),
      regions: new Map<string, string>(),
    };
    current.stockAvailable += stockAvailable;
    if (buyerMicros > 0) {
      if (current.bestBuyerMicros <= 0 || buyerMicros < current.bestBuyerMicros) {
        current.bestHourlyPrice = buyerHourlyPrice;
        current.bestBuyerMicros = buyerMicros;
      }
    } else if (current.bestBuyerMicros <= 0 && (current.bestHourlyPrice <= 0 || rawHourlyPrice < current.bestHourlyPrice)) {
      current.bestHourlyPrice = rawHourlyPrice;
    }

    const rawProvider = String(
      offer.provider_name || offer.provider_slug || offer.source || "",
    ).trim();
    if (rawProvider) {
      const slug = walletProviderSlugFromLabel(rawProvider);
      const label = slug ? walletProviderLabel(slug) : rawProvider;
      current.providers.set((slug || rawProvider).toLowerCase(), label);
    }
    const region = String(offer.region_name || "").trim();
    if (region) current.regions.set(region.toLowerCase(), region);
    byHardware.set(key, current);
  });

  return [...byHardware.values()]
    .map((entry) => ({
      hardware_id: entry.hardwareID,
      hardware_label: entry.hardwareLabel,
      gpu_count: entry.gpuCount,
      stock_available: entry.stockAvailable,
      launchable: true,
      capacity_status: "available" as const,
      best_price_per_hour: entry.bestHourlyPrice,
      best_buyer_price_micros: entry.bestBuyerMicros,
      provider_count: entry.providers.size,
      providers: walletMarketUniqueValues(entry.providers.values()),
      regions: walletMarketUniqueValues(entry.regions.values()),
    }))
    .sort((left, right) =>
      left.best_price_per_hour - right.best_price_per_hour ||
      left.hardware_label.localeCompare(right.hardware_label) ||
      left.gpu_count - right.gpu_count,
    );
}

export function resolveWalletRunHardwareOptions<T extends WalletProviderHardwarePayloadLike>(
  providerOptions: readonly T[] | null | undefined,
  freshMarketOffers: readonly WalletMarketOfferLike[] | null | undefined,
  previousOptions: readonly T[] = [],
): Array<T | WalletMarketHardwareOption> {
  if (providerOptions == null && freshMarketOffers == null) return [...previousOptions];
  const aggregateOptions = [...(providerOptions || [])];
  const liveAggregateKeys = new Set(
    aggregateOptions
      .filter((option) => walletProviderHardwareOptionIsLivePriced(option))
      .map(walletProviderHardwareOptionKey)
      .filter(Boolean),
  );
  const marketFallback = walletHardwareOptionsFromMarketOffers(freshMarketOffers)
    .filter((option) => option.gpu_count === 1)
    .filter((option) => !liveAggregateKeys.has(walletProviderHardwareOptionKey(option)));
  return [...aggregateOptions, ...marketFallback];
}

export const WALLET_PROVIDER_OPTIONS: Array<{ slug: WalletProviderSlug; label: string }> = [
  { slug: "hyperstack", label: "Hyperstack" },
  { slug: "lambda", label: "Lambda" },
  { slug: "runpod", label: "Runpod" },
];

export function walletProviderSlugFromLabel(value: unknown): WalletProviderSlug | "" {
  const token = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[_\s]+/g, "-");
  if (!token || walletProviderValueIsManagedRoute(token)) return "";
  if (token.includes("hyperstack") || token.includes("nexgen")) return "hyperstack";
  if (token.includes("runpod") || token.includes("run-pod")) return "runpod";
  if (token === "lambda" || token.includes("lambda-cloud") || token.includes("lambda-labs"))
    return "lambda";
  if (
    token === "gcp" ||
    token === "googlecloud" ||
    token === "gce" ||
    token.includes("google-cloud") ||
    token.includes("google-compute-engine")
  )
    return "gcp";
  if (
    token === "azure" ||
    token === "azure-cloud" ||
    token === "microsoftazure" ||
    token.includes("microsoft-azure") ||
    token.includes("azure-compute")
  )
    return "azure";
  const normalized = token.replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
  return normalized.length <= 64 ? normalized : "";
}

export function walletProviderLabel(slug: unknown) {
  const normalized = walletProviderSlugFromLabel(slug);
  const known = WALLET_PROVIDER_OPTIONS.find((option) => option.slug === normalized)?.label;
  if (known) return known;
  if (normalized === "gcp") return "Google Cloud";
  return normalized
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export function uniqueWalletProviderSlugs(values: readonly unknown[]) {
  const seen = new Set<WalletProviderSlug>();
  const out: WalletProviderSlug[] = [];
  values.forEach((value) => {
    const slug = walletProviderSlugFromLabel(value);
    if (!slug || seen.has(slug)) return;
    seen.add(slug);
    out.push(slug);
  });
  const known = WALLET_PROVIDER_OPTIONS.map((option) => option.slug).filter((slug) =>
    seen.has(slug),
  );
  const knownSet = new Set(known);
  const discovered = out
    .filter((slug) => !knownSet.has(slug))
    .sort((left, right) => left.localeCompare(right));
  return [...known, ...discovered];
}

export function walletProviderValueIsManagedRoute(value: unknown) {
  const token = String(value || "")
    .trim()
    .toLowerCase();
  return /\b(pro-v1|genrl|managed)\b/.test(token);
}

function walletProAccessToken(value: unknown) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
}

function walletProAccessMatches(
  access: WalletProProviderAccessLike,
  mode: unknown,
  clusterProfile?: unknown,
) {
  if (walletProAccessToken(access.mode) !== walletProAccessToken(mode)) return false;
  const profile = walletProAccessToken(clusterProfile);
  if (!profile || walletProAccessToken(mode) !== "cluster") return true;
  return (access.clusterTypes || []).some((value) => walletProAccessToken(value) === profile);
}

function walletProAccessProviderValues(
  option: WalletProProviderHardwareLike,
  mode: unknown,
  clusterProfile?: unknown,
) {
  const accessOptions = option.proAccessOptions || [];
  const modeOptions = accessOptions.filter(
    (access) => walletProAccessToken(access.mode) === walletProAccessToken(mode),
  );
  const matchingOptions = modeOptions.filter((access) =>
    walletProAccessMatches(access, mode, clusterProfile),
  );
  // A declared mode with no matching cluster profile is an explicit mismatch,
  // not a reason to fall back to the aggregate provider list.
  if (modeOptions.length > 0 && matchingOptions.length === 0) return [];
  const accessProviders = matchingOptions
    .flatMap((access) => access.providers || [])
    .filter((value) => walletProviderSlugFromLabel(value));
  return accessProviders.length > 0 ? accessProviders : option.providers || [];
}

export function walletProProviderChoicesForAccess(
  options: readonly WalletProProviderHardwareLike[],
  mode: unknown,
  clusterProfile?: unknown,
) {
  return uniqueWalletProviderSlugs(
    options.flatMap((option) => walletProAccessProviderValues(option, mode, clusterProfile)),
  );
}

export function walletProHardwareSupportsProvider(
  option: WalletProProviderHardwareLike,
  providerSlug: unknown,
  mode: unknown,
  clusterProfile?: unknown,
) {
  const selected = walletProviderSlugFromLabel(providerSlug);
  if (!selected) return false;
  return walletProAccessProviderValues(option, mode, clusterProfile).some(
    (value) => walletProviderSlugFromLabel(value) === selected,
  );
}

export function walletProProviderHardwareRequestPath(input: {
  mode: unknown;
  providerSlug?: unknown;
  clusterProfile?: unknown;
  refresh?: boolean;
}) {
  const params = new URLSearchParams({
    pro_access_mode: walletProAccessToken(input.mode),
    refresh: input.refresh === false ? "false" : "true",
  });
  const provider = walletProviderSlugFromLabel(input.providerSlug);
  if (provider) params.set("provider_slug", provider);
  const profile = String(input.clusterProfile || "").trim().toLowerCase();
  if (profile) params.set("cluster_type", profile);
  return `${WALLET_PROVIDER_HARDWARE_API_PATH}?${params.toString()}`;
}

export function walletManagedProviderHardwareRequestPath(providerSlug: unknown) {
  const params = new URLSearchParams();
  const provider = walletProviderSlugFromLabel(providerSlug);
  if (provider) params.set("provider_slug", provider);
  // A global offer cache can be non-empty while this provider has no cached
  // rows yet, so a newly connected provider must refresh on first selection.
  params.set("refresh", "true");
  return `${WALLET_PROVIDER_HARDWARE_API_PATH}?${params.toString()}`;
}

export function walletProviderHardwareOptionsFromPayload<T>(payload: unknown): T[] {
  const response = payload as { hardware_options?: unknown; refresh_error?: unknown } | null;
  if (!response || !Array.isArray(response.hardware_options)) {
    throw new Error(
      "Invalid provider hardware response: expected hardware_options to be an array.",
    );
  }
  if (response.hardware_options.length === 0) {
    const refreshError =
      typeof response.refresh_error === "string" ? response.refresh_error.trim() : "";
    if (refreshError) throw new Error(refreshError);
  }
  return response.hardware_options as T[];
}

export function walletScopedProviderHardwareOptions<T>(
  responseProviderSlug: unknown,
  selectedProviderSlug: unknown,
  options: readonly T[],
) {
  const responseProvider = walletProviderSlugFromLabel(responseProviderSlug);
  const selectedProvider = walletProviderSlugFromLabel(selectedProviderSlug);
  return responseProvider && responseProvider === selectedProvider ? [...options] : [];
}

export function walletPinProAccessProvider<T extends WalletProProviderAccessLike>(
  access: T | null | undefined,
  providerSlug: unknown,
): T | null {
  if (!access) return null;
  const selected = walletProviderSlugFromLabel(providerSlug);
  if (!selected) return null;
  const advertisedLabel = (access.providers || []).find(
    (value) => walletProviderSlugFromLabel(value) === selected,
  );
  const provider = String(advertisedLabel || walletProviderLabel(selected) || selected).trim();
  return {
    ...access,
    providers: [provider],
    resolvedParameters: {
      ...(access.resolvedParameters || {}),
      provider,
    },
  } as T;
}

export function walletProviderLaunchHardwareLabel(
  _providerSlug: unknown,
  hardwareLabel: unknown,
  _providers: readonly unknown[],
) {
  const label = String(hardwareLabel || "").trim();
  return label;
}
